import { EventBus } from "../EventBus.js";
import type { PlayerCharacter } from "../types/entity.js";
import type { SRDClass } from "../types/srd.js";
import { processLevelUps } from "./xpProgression.js";

/**
 * XP → level-up bridge (W09).
 *
 * Subscribes to `world:xp_awarded`, runs each party member through the
 * `processLevelUps` resolver, writes the upgraded character back via the
 * caller-supplied mutator, and emits a `world:level_up` event per level
 * gained. The HUD turns those events into feed lines and queues the ASI
 * modal for jumps that require it.
 *
 * The watcher is pure glue — dependencies (class lookup, party fetch,
 * member writeback) are injected so tests can exercise the full loop
 * without touching the real PartyStore.
 */

export interface LevelUpWatcherDeps {
  getMembers: () => readonly PlayerCharacter[];
  /** Resolve a character's class record. Return `null` to skip that member. */
  getClassData: (characterClass: string) => SRDClass | null;
  /** Apply `patch` to the named member (typically PartyStore.updateMember). */
  updateMember: (instanceId: string, patch: Partial<PlayerCharacter>) => void;
}

export function createLevelUpWatcher(deps: LevelUpWatcherDeps): { dispose: () => void } {
  const off = EventBus.on("world:xp_awarded", () => {
    for (const member of deps.getMembers()) {
      const classData = deps.getClassData(member.characterClass);
      if (!classData) continue;
      const { character, summaries } = processLevelUps(member, classData);
      if (summaries.length === 0) continue;

      deps.updateMember(member.instanceId, {
        level: character.level,
        proficiencyBonus: character.proficiencyBonus,
        hp: character.hp,
        maxHp: character.maxHp,
        spellSlots: character.spellSlots,
      });

      for (const summary of summaries) {
        EventBus.emit("world:level_up", {
          instanceId: member.instanceId,
          name: member.name,
          newLevel: summary.newLevel,
          hpGained: summary.hpGained,
          proficiencyAfter: summary.proficiencyAfter,
          slotsAdded: summary.slotsAdded,
          asiRequired: summary.asiRequired,
        });
      }
    }
  });

  return { dispose: off };
}
